import { countryLabelKey, type CountryCode } from '@aysar/contracts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDirection } from '@/shared/hooks/useDirection/useDirection';

import { countryOptions } from '../country-options/country-options.util';
import { type SelectionOption } from '../types/selection-option.types';

export interface UseCountrySelectLogic {
  options: readonly SelectionOption[];
  placeholder: string;
  searchPlaceholder: string;
  emptyLabel: string;
}

/**
 * The 249 rows of the ISO country list, named and ordered for the locale on
 * screen.
 *
 * **Memoised on the locale**, because the alternative is measurable: building
 * the list means 249 `Intl.DisplayNames` lookups and a full collation, and this
 * control's whole job is to be typed into. Rebuilding it per keystroke is the
 * stall an operator would feel.
 *
 * The catalog is consulted **first** for every code (`countryLabelKey`), so a
 * deployment can override any name without touching code — the seam
 * `@aysar/contracts` documents and the reason the shipped catalog carries one
 * real override rather than an empty object.
 */
export function useCountrySelectLogic(): UseCountrySelectLogic {
  const { t } = useTranslation('uiKit');
  const { locale } = useDirection();

  const options = useMemo(
    () =>
      countryOptions(locale, (code: CountryCode) =>
        // `defaultValue: ''` rather than letting i18next echo the key back: a
        // missing override must read as "the catalog said nothing", and
        // `country.SY` printed as a country name is the exact failure U8's
        // missing-key behaviour is otherwise useful for.
        t(countryLabelKey(code), { defaultValue: '' }),
      ),
    [locale, t],
  );

  return useMemo(
    () => ({
      options,
      placeholder: t('countrySelect.placeholder'),
      searchPlaceholder: t('countrySelect.searchPlaceholder'),
      emptyLabel: t('countrySelect.empty'),
    }),
    [options, t],
  );
}
